import { MenuSubItem } from "@/components/header-group/components/MenuSubItem"
import { processMenuUrl } from "@/components/header-group/utils"
import { StructuredMenu } from "@/components/header-one/menu-structure"
import { languages } from "@/configs"
import { Images } from "@/constants/images"
import { getLocaleHomeHref } from "@/lib/locale-utils"
import { CaretDownOutlined } from "@ant-design/icons"
import { Menu, X } from "lucide-react"
import Image from "next/image"
import Link from "next/link"

type HeaderMobileProps = {
  locale: string
  structuredMenu: StructuredMenu
  isMobileMenuOpen: boolean
  isMenuClosing: boolean
  openSubMenus: Set<number>
  localeLinks: Record<string, string>
  onMenuIconClick: () => void
  onCloseMenu: () => void
  onToggleSubMenu: (itemId: number) => void
}

export function HeaderMobile({
  locale,
  structuredMenu,
  isMobileMenuOpen,
  isMenuClosing,
  openSubMenus,
  localeLinks,
  onMenuIconClick,
  onCloseMenu,
  onToggleSubMenu,
}: HeaderMobileProps) {
  const homeHref = getLocaleHomeHref(locale)

  return (
    <div
      className="fixed top-0 left-0 right-0 block lg:hidden z-50 bg-white"
      style={{ top: "var(--admin-bar-h, 0px)" }}
    >
      <div className="items-center py-5 relative">
        <div className="flex justify-between items-center w-full h-[70px]">
          <div className="px-[15px] cursor-pointer" onClick={onMenuIconClick}>
            <Menu color="#18af4e" size={33} strokeWidth={1.5} />
          </div>
          <Link className="w-[220px] h-[26px] mx-[15px] flex items-center justify-center" href={homeHref}>
            <Image alt="Logo" className="w-[180px] h-full object-contain" src={Images.logoTamdaGroup} priority />
          </Link>
          <div className="flex justify-end items-center gap-4 justify-items-center">
            <div className="relative group pr-[15px]">
              <div className="cursor-pointer flex items-center justify-center gap-[5px]">
                <div className="w-5 h-6">
                  <Image
                    src={locale === "en" ? Images.logoEn : Images.logoVi}
                    alt={`logo-${locale}`}
                    height={20}
                    width={20}
                    className="w-full h-full"
                  />
                </div>
                <span className="text-base leading-5 font-bold text-[#242f3e]">{locale === "en" ? "EN" : "VN"}</span>
              </div>
              <ul
                className="absolute right-[15px] top-10 bg-white shadow-lg z-50 min-w-[150px] opacity-0 translate-y-10 pointer-events-none transition-all duration-700 ease-out group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto shadow-custom-lg"
                aria-hidden="true"
              >
                {languages.map(lang => (
                  <li key={lang.code}>
                    <Link href={localeLinks[lang.code] ?? lang.href} className="flex items-center gap-1 px-4 py-3 hover:bg-gray-100">
                      <div className="w-5 h-6">
                        <Image src={lang.logo} alt={`logo-${lang.code}`} height={20} width={20} />
                      </div>
                      <span className="text-sm leading-5 font-medium text-[#242f3e]">{lang.shortLabel}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        {isMobileMenuOpen && (
          <>
            <div className="fixed inset-0 bg-black bg-opacity-90 z-40" onClick={onCloseMenu} />
            <div
              className={`fixed top-0 left-0 w-[80%] h-[100vh] bg-[#18af4e] shadow-lg z-50 ${isMenuClosing ? "mobile-menu-slide-out" : "mobile-menu-slide-in"
                }`}
            >
              <div className="h-[85px] border-b-[3px] border-white flex items-center justify-between px-[15px]">
                <Link href={homeHref} className="w-full flex justify-center">
                  <Image
                    src={Images.logoTamdaGroupMobile}
                    alt="Logo"
                    className="w-[180px] h-[43px] object-contain"
                    priority
                  />
                </Link>
                <button onClick={onCloseMenu} className="text-white">
                  <X size={32} strokeWidth={2} className="w-10 h-10" />
                </button>
              </div>
              <ul className="flex flex-col py-5 overflow-y-auto h-[calc(100vh-70px)]">
                {structuredMenu?.map((item, index) => {
                  const hasSubmenu = item.attribute && item.attribute.length > 0
                  const { finalUrl, isExternal } = processMenuUrl(item.url, locale)

                  return (
                    <li key={item.id}>
                      <div className="flex items-center justify-between px-10 py-6">
                        {isExternal ? (
                          <Link
                            href={finalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 text-white text-base leading-[22px] font-bold uppercase"
                            onClick={() => {
                              if (!hasSubmenu) {
                                onCloseMenu()
                              }
                            }}
                          >
                            <span className="text-base leading-[22px] font-medium uppercase text-white">
                              {item.title}
                            </span>
                          </Link>
                        ) : (
                          <Link
                            href={finalUrl}
                            className="flex-1 text-white text-base leading-[22px] font-bold uppercase"
                            onClick={e => {
                              if (hasSubmenu) {
                                if (openSubMenus.has(item.id)) {
                                  onCloseMenu()
                                } else {
                                  e.preventDefault()
                                  onToggleSubMenu(item.id)
                                }
                              } else {
                                onCloseMenu()
                              }
                            }}
                          >
                            <span className="text-base leading-[22px] font-medium uppercase text-white">
                              {item.title}
                            </span>
                          </Link>
                        )}
                        {item.attribute && item.attribute.length > 0 && (
                          <button
                            onClick={e => {
                              e.stopPropagation()
                              onToggleSubMenu(item.id)
                            }}
                            className="text-white px-6 transition-opacity"
                          >
                            <CaretDownOutlined
                              className={`text-lg leading-5 transition-transform duration-300 ${openSubMenus.has(item.id) ? "rotate-180" : ""
                                }`}
                            />
                          </button>
                        )}
                      </div>
                      {item.attribute && item.attribute.length > 0 && (
                        <div
                          className={`overflow-hidden transition-all duration-500 ${openSubMenus.has(item.id) ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
                            }`}
                        >
                          <ul className="bg-[#18af4e]">
                            {item.attribute.map((subItem, subIndex) => (
                              <li key={subIndex}>
                                <MenuSubItem
                                  subItem={subItem}
                                  locale={locale}
                                  className="block px-[60px] py-3 text-white hover:text-white text-[15px] leading-[24px] font-normal uppercase"
                                  onClick={onCloseMenu}
                                />
                                {item.attribute && subIndex < item.attribute.length - 1 && (
                                  <div className="h-px bg-white bg-opacity-20 mx-8" />
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {index < structuredMenu.length - 1 && <div className="h-px bg-white bg-opacity-20 mx-10" />}
                    </li>
                  )
                })}
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
